import { useContext, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import MyButton from "../../../components/MyButton";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { PopupContext } from "../../../context/PopupProvider";
import DeleteBoardItemDialog from "../../Common/DeleteBoardItemDialog";
import MyLoading from "../../../components/MyLoading";
import Comment from "../../Comment";
import { AuthContext } from "../../../context/AuthProvider";
import EnterWriterPasswordDialog from "../../Common/EnterWriterPasswordDialog";
import { SiteContext } from "../../../context/SiteProvider";
import FileItem from "../../../components/inputs/MyInputFile/FileItem";
import { renderBody } from "../../../utils";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";

const FamilyDetail0234 = ({ boardCode, parent }) => {
  const { itemNo } = useParams();
  const {
    useGetBoardItem,
    useGetBoard,
    deleteBoardItemMutation,
    verifyPasswordEditBoardMutation,
    verifyPasswordViewBoardMutation,
  } = useBoardItem();
  const navigate = useNavigate();
  const { isAuthenticated, profile, isAdmin, roleCode } =
    useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);
  const { setLoadingScreen } = useContext(SiteContext);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading, isError, error } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      includeAdjacent: true,
      enabled: checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.READ,
        nonPermissions,
        isAdmin
      ),
    },
    (data) => data.data
  );

  const openDeleteDialog = () =>
    openPopup(
      <DeleteBoardItemDialog
        boardCode={board?.boardCode}
        itemNo={itemNo}
        onRedirect={() => {
          navigate(`/Family/${parent}`);
        }}
      />
    );

  const openPopupGoToEditPageOfNonMember = () => {
    openPopup(
      <EnterWriterPasswordDialog
        params={{ boardCode: board?.boardCode, itemNo }}
        permission="edit"
        onAction={(res) => {
          if (res?.privateViewToken) {
            verifyPasswordEditBoardMutation.mutate(
              {
                boardCode: board?.boardCode,
                itemNo,
                privateViewToken: res?.privateViewToken,
                payload: res?.payload,
              },
              {
                onSuccess: (res2) => {
                  if (res2?.anonymousEditToken) {
                    navigate(
                      `/Family/${parent}/Edit/${itemNo}?privateViewToken=${res.privateViewToken}&anonymousEditToken=${res2.anonymousEditToken}`
                    );
                  }
                },
              }
            );
          }
        }}
        mutation={verifyPasswordViewBoardMutation}
      />
    );
  };

  const openPopupDeleteOfNonMember = () => {
    openPopup(
      <EnterWriterPasswordDialog
        params={{ boardCode: board?.boardCode, itemNo }}
        permission="delete"
        onAction={(res) => {
          if (res?.privateViewToken) {
            setLoadingScreen(true);
            verifyPasswordEditBoardMutation.mutate(
              {
                boardCode: board?.boardCode,
                itemNo,
                privateViewToken: res?.privateViewToken,
                payload: res?.payload,
              },
              {
                onSuccess: (res2) => {
                  if (res2?.anonymousEditToken) {
                    deleteBoardItemMutation.mutate(
                      {
                        boardCode,
                        itemNo,
                        privateViewToken: res.privateViewToken,
                      },
                      {
                        onSuccess: () => {
                          alert(t("common.messages.boardItem.deleteSuccess"));
                          queryClient.invalidateQueries({
                            queryKey: ["/board-item?boardCode={boardCode}"],
                          });
                          navigate(`/Family/${parent}`);
                        },
                        onError: () => {
                          alert(t("common.messages.boardItem.error"));
                        },
                        onSettled: () => {
                          setLoadingScreen(false);
                        },
                      }
                    );
                  }
                },
              }
            );
          }
        }}
        mutation={verifyPasswordViewBoardMutation}
      />
    );
  };

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.READ,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.READ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.READ}
      boardCode={boardCode}
    >
      <div className="border-t-2 border-primary pt-6">
        {isLoading ? (
          <MyLoading className="h-[150px]" />
        ) : isError && !data ? (
          <p className="text-center text-lg text-gray-dark pt-10">
            {error?.data?.code === "NO_PERMISSION"
              ? "이 글은 비밀글입니다. 작성자 및 관리자만 열람하실 수 있습니다."
              : "이 게시물의 정보를 볼 수 없습니다."}
          </p>
        ) : (
          <>
            <h2 className="font-semibold mb-2 2xl:text-[32px] md:text-[28px] text-[22px]">
              {data?.title}
            </h2>
            <div className="flex items-center md:gap-8 gap-6 mb-6">
              <p className="flex items-center md:gap-2 gap-[6px] text-[#767676] 2xl:text-lg md:text-base text-sm">
                {data?.writer?.name}
                <span className="h-2 w-[1px] bg-[#767676]" />
                {dayjs(data?.createdDate).format("YYYY-MM-DD")}
              </p>
              <p className="flex items-center md:gap-2 gap-[6px] text-[#767676] 2xl:text-lg md:text-base text-sm">
                조회수
                <span className="h-2 w-[1px] bg-[#767676]" />
                {data?.readCount || 0}
              </p>
            </div>
            <div className="py-6 border-t border-b border-gray-border min-h-36">
              {data?.body ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderBody(data.body, data.embeddedFiles),
                  }}
                  className="body-text"
                />
              ) : (
                ""
              )}
            </div>
            <ul>
              <li className="border-b border-gray-border p-4 flex items-start gap-4">
                <div className="flex items-center w-[68px]">
                  <span
                    className={`font-semibold 2xl:text-lg text-base ${
                      !data?.attachedFiles?.length ? "text-[#999]" : ""
                    }`}
                  >
                    첨부파일
                  </span>
                </div>

                <div className="bg-gray-border h-3 w-[1px] mt-[6px]" />

                <div className="flex-1">
                  {data?.attachedFiles?.length > 0 ? (
                    <>
                      {data.attachedFiles.map((item) => (
                        <FileItem key={item.fileId} file={item} />
                      ))}
                    </>
                  ) : (
                    <span className="text-[#999] 2xl:text-lg text-base">-</span>
                  )}
                </div>
              </li>
              <li className="border-b border-gray-border p-4 flex items-center gap-4">
                <div className="flex items-center w-[68px]">
                  <span
                    className={`font-semibold 2xl:text-lg text-base ${
                      !data?.nextItem ? "text-[#999]" : ""
                    }`}
                  >
                    다음글
                  </span>
                </div>

                <div className="bg-gray-border h-3 w-[1px]" />

                <div className="flex-1">
                  {data?.nextItem ? (
                    <Link
                      to={`/News/Notice/${data.nextItem.itemNo}`}
                      className="text-gray-dark 2xl:text-lg text-base"
                    >
                      {data.nextItem?.title}
                    </Link>
                  ) : (
                    <span className="text-[#999] 2xl:text-lg text-base">-</span>
                  )}
                </div>
              </li>
              <li className="border-b border-gray-border p-4 flex items-center gap-4">
                <div className="flex items-center w-[68px]">
                  <span
                    className={`font-semibold 2xl:text-lg text-base ${
                      !data?.prevItem ? "text-[#999]" : ""
                    }`}
                  >
                    이전글
                  </span>
                </div>

                <div className="bg-gray-border h-3 w-[1px]" />

                <div className="flex-1">
                  {data?.prevItem ? (
                    <Link
                      to={`/News/Notice/${data.prevItem.itemNo}`}
                      className="text-gray-dark 2xl:text-lg text-base"
                    >
                      {data.prevItem?.title}
                    </Link>
                  ) : (
                    <span className="text-[#999] 2xl:text-lg text-base">-</span>
                  )}
                </div>
              </li>
            </ul>

            <div className="flex items-center mt-10 sm:gap-4 gap-2">
              {checkRoleAndPermissions(
                roleCode,
                board,
                BOARD_PERMISSION.ACCESS,
                nonPermissions,
                isAdmin
              ) && (
                <MyButton
                  variant="contained"
                  color="#111111"
                  className="font-semibold sm:min-w-[120px] mr-auto text-white"
                  onClick={() => navigate(`/Family/${parent}`)}
                >
                  목록보기
                </MyButton>
              )}

              {checkRoleAndPermissions(
                roleCode,
                board,
                BOARD_PERMISSION.WRITE,
                nonPermissions,
                isAdmin
              ) && (
                <>
                  {isAuthenticated &&
                  (profile?.userId === data?.writer?.userId || isAdmin) ? (
                    // actions for members
                    <>
                      <MyButton
                        variant="outlined"
                        className="sm:min-w-[120px]"
                        onClick={openDeleteDialog}
                      >
                        <span className="font-semibold">삭제</span>
                      </MyButton>
                      <MyButton
                        variant="contained"
                        color="primary"
                        className="font-semibold sm:min-w-[120px]"
                        onClick={() =>
                          navigate(`/Family/${parent}/Edit/${itemNo}`)
                        }
                      >
                        수정
                      </MyButton>
                    </>
                  ) : !isAuthenticated ? (
                    // action for non-members
                    <>
                      <MyButton
                        variant="outlined"
                        className="sm:min-w-[120px]"
                        onClick={openPopupDeleteOfNonMember}
                      >
                        <span className="font-semibold">삭제</span>
                      </MyButton>
                      <MyButton
                        variant="contained"
                        color="primary"
                        className="font-semibold sm:min-w-[120px]"
                        onClick={openPopupGoToEditPageOfNonMember}
                      >
                        수정
                      </MyButton>
                    </>
                  ) : null}
                </>
              )}
            </div>

            <Comment
              board={board}
              boardItem={data}
              parent={parent}
              className="mt-10"
            />
          </>
        )}
      </div>
    </CheckRoleAndPermissionDisplay>
  );
};

export default FamilyDetail0234;
